import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Lock, MapPin, Moon, ShowerHead } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { OpenStatus } from "@/components/site/OpenStatus";
import { Testimonials, Reviews, InstagramFeed } from "@/components/site/Social";
import { usePageMeta } from "@/lib/seo";
import { money } from "@/lib/api";

const FEATURES = [
  { icon: Moon, title: "Open Late", text: "Until 2 AM on weekdays, midnight on weekends." },
  { icon: MapPin, title: "Two Locations", text: "Bayside and Flushing. One membership." },
  { icon: ShowerHead, title: "Showers", text: "Hit it before work, shower, go." },
  { icon: Lock, title: "Lockers", text: "Lock up your stuff and train." },
  { icon: Flame, title: "Sauna", text: "Recover in the sauna at our Flushing gym.", perk: true },
];

function Hero() {
  const { content } = useSite();
  return (
    <section className="grain relative flex min-h-[calc(100svh-90px)] items-end overflow-hidden" data-testid="home-hero">
      <motion.img src="/images/hero.webp" alt="Iron Pump Gym weight floor" fetchPriority="high" initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: "easeOut" }}
        className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
      <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-24 sm:px-8 sm:pb-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="max-w-4xl space-y-6">
          <span className="inline-flex items-center gap-2 rounded-sm border border-[var(--gold)]/60 bg-black/50 px-3 py-1.5 font-mono-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--gold)] backdrop-blur" data-testid="hero-badge">
            <Moon className="h-3.5 w-3.5" /> {content.settings.hero_badge}
          </span>
          <h1 className="h-display text-[17vw] text-white sm:text-8xl lg:text-[9.5rem]" data-testid="hero-headline">
            2 Gyms.<br /><span className="text-gold-grad">1 Membership.</span>
          </h1>
          <p className="max-w-xl text-base text-white/80 md:text-lg" data-testid="hero-subheading">Train in Bayside & Flushing with one membership.</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/memberships" className="btn-gold" data-testid="hero-view-memberships-btn">View Memberships <ArrowRight className="h-5 w-5" /></Link>
            <Link to="/locations" className="btn-ghost" data-testid="hero-visit-us-btn">Visit Us</Link>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {content.locations.map((l) => <OpenStatus key={l.id} loc={l} compact />)}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const words = ["Bayside", "Flushing", "Open Til 2 AM", "2 Gyms", "1 Membership", "Sauna in Flushing"];
  const row = [...words, ...words];
  return (
    <div className="overflow-hidden border-y border-white/10 bg-[var(--gold)] py-3" aria-hidden="true">
      <div className="animate-marquee flex w-max gap-10 whitespace-nowrap">
        {[...row, ...row].map((w, i) => <span key={i} className="h-display text-2xl text-black">{w} <span className="ml-10">✦</span></span>)}
      </div>
    </div>
  );
}

function Intro() {
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 sm:py-28 lg:grid-cols-2" data-testid="home-intro">
      <SectionHeading eyebrow="Your neighborhood gym" title={<>No ego.<br />Just iron.</>}>
        Iron Pump is a no-nonsense lifting gym for Bayside and Flushing. Heavy weights, plenty of racks, and hours that work with real schedules.
        First time in a gym? Perfect. Our front desk will show you around, and our regulars were beginners once too.
      </SectionHeading>
      <Reveal delay={0.15} className="relative">
        <img src="/images/group.webp" alt="Members training at Iron Pump Gym" loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-md object-cover" />
        <div className="glass absolute -bottom-6 left-4 rounded-md px-5 py-4 sm:-left-6">
          <p className="h-display text-4xl text-[var(--gold)]">7 days</p>
          <p className="font-mono-ui text-[11px] uppercase tracking-wider text-white/70">a week, early to late</p>
        </div>
      </Reveal>
    </section>
  );
}

function Features() {
  return (
    <section className="border-y border-white/10 bg-[#0E0E0E]" data-testid="home-features">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-white/10 sm:grid-cols-3 lg:grid-cols-5">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.07} className={`bg-[#0E0E0E] p-6 sm:p-8 ${i === 4 ? "col-span-2 sm:col-span-1" : ""}`}>
            <div data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <f.icon className={`h-8 w-8 ${f.perk ? "text-[var(--gold)]" : "text-white"}`} />
              <p className="h-display mt-5 text-2xl text-white">{f.title}{f.perk && <span className="ml-2 align-middle font-mono-ui text-[10px] tracking-wider text-[var(--gold)]">PERK</span>}</p>
              <p className="mt-2 text-sm text-white/55">{f.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function PriceTeaser() {
  const { content } = useSite();
  const min = Math.min(...content.plans.filter((p) => p.suffix).map((p) => p.offpeak_price ?? p.peak_price));
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8" data-testid="home-price-teaser">
      <Reveal>
        <div className="stripe-bg flex flex-col items-start justify-between gap-6 rounded-md border border-[var(--gold)]/30 p-8 sm:p-12 md:flex-row md:items-center">
          <div>
            <p className="eyebrow">Memberships</p>
            <p className="h-display mt-2 text-5xl text-white sm:text-6xl">From <span className="text-gold-grad">{money(min)}</span>/month</p>
            <p className="mt-2 text-sm text-white/60">No cancellation fees. Access to both gyms.</p>
          </div>
          <Link to="/memberships" className="btn-gold" data-testid="teaser-memberships-btn">See All Plans <ArrowRight className="h-5 w-5" /></Link>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  usePageMeta(
    "Iron Pump Gym | Gym in Bayside & Flushing, Queens | Open Until 2 AM",
    "Late-night gym in Bayside and Flushing, Queens. 2 gyms, 1 membership, showers, lockers and a sauna in Flushing. Cheap gym memberships from $60/month."
  );
  return (
    <>
      <Hero />
      <Marquee />
      <Intro />
      <Features />
      <PriceTeaser />
      <Testimonials />
      <Reviews />
      <InstagramFeed />
    </>
  );
}
